import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import { DashboardLayout } from "components/DashboardLayout";
import { TableUI } from "components/design/TableUI";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useDebounce } from "utils/debounce";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  IncorporationTrackerRow,
  useGetIncorporationTrackerQuery,
} from "store/apis/incorporation";
import { GroupNote } from "components/BooksCrm/GroupNote";
import { useModal } from "hooks/useModal";
import { AssignTemplateModal } from "./AssignTemplateModal";
import { MobileIncorporationTrackerRow } from "./MobileIncorporationTrackerRow";
import { useWindowSize } from "hooks/useWindowSize";
import { Link } from "components/DesignSystem/Link/Link";
import { useHistory } from "react-router-dom";
import { useState, MouseEvent } from "react";
import { pluralize } from "utils/pluralize";
import { Tag } from "components/DesignSystem/Tag/Tag";
import ToolTip from "components/design/toolTip";

const PAGE_SIZE = 20;

const IncorporationTrackerNameCell = ({
  groupId,
  groupName,
}: {
  groupId: string;
  groupName: string;
}) => {
  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Link
      to={`/admin/crm/${groupId}`}
      onClick={handleClick}
      className="t-truncate t-text-body-sm"
    >
      {groupName}
    </Link>
  );
};

const IncorporationTrackerTemplateCell = ({
  formInstanceId,
  displayTitle,
  isPublished,
  onAssignClick,
  onEditDraftClick,
}: {
  formInstanceId: string | null;
  displayTitle: string | null;
  isPublished: boolean;
  onAssignClick: () => void;
  onEditDraftClick: () => void;
}) => {
  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  if (!formInstanceId) {
    return (
      <Button
        type="button"
        customType="primary"
        size="xsmall"
        onClick={(e) => {
          handleClick(e);
          onAssignClick();
        }}
      >
        Assign template
      </Button>
    );
  }

  if (!isPublished) {
    return (
      <Button
        type="button"
        customType="primary"
        size="xsmall"
        onClick={(e) => {
          handleClick(e);
          onEditDraftClick();
        }}
      >
        View & Edit draft
      </Button>
    );
  }

  if (!displayTitle) {
    return <span className="t-block t-truncate t-text-subtext">—</span>;
  }

  return (
    <ToolTip text={displayTitle}>
      <span className="t-block t-truncate t-text-body-sm">{displayTitle}</span>
    </ToolTip>
  );
};

const IncorporationTrackerStageCell = ({
  currentStageTitle,
  answeredQuestionsCount,
  totalQuestionsCount,
  isCompleted,
}: {
  currentStageTitle: string | null;
  answeredQuestionsCount: number;
  totalQuestionsCount: number;
  isCompleted: boolean;
}) => {
  if (!currentStageTitle) {
    return (
      <div className="t-text-body-sm-italic t-italic t-text-text-30">
        Not started
      </div>
    );
  }

  if (isCompleted) {
    return (
      <Tag rounded icon={false} tagType="green">
        Completed
      </Tag>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-0.5">
      <span className="t-text-body-sm t-text-text-100">
        <Tag rounded icon={false} tagType="gray">
          {currentStageTitle}
        </Tag>
      </span>
      <span className="t-text-body-sm-italic t-italic t-text-text-30">
        {answeredQuestionsCount} of{" "}
        {pluralize(totalQuestionsCount, "question", "questions")} completed
      </span>
    </div>
  );
};

export const IncorporationTracker = () => {
  usePageTitle("Incorporation Tracker");
  const history = useHistory();
  const { isMobile } = useWindowSize();
  const query = useQuery();
  const { update } = useUpdateQuery();
  const assignModal = useModal();

  const searchValue = query.get("search");
  const searchTerm = useDebounce(searchValue);
  const pageNumber = Number(query.get("page")) || 1;

  const [selectedRow, setSelectedRow] =
    useState<IncorporationTrackerRow | null>(null);

  const { data, isLoading, isSuccess } = useGetIncorporationTrackerQuery({
    search: searchTerm,
    page_num: pageNumber,
    page_size: PAGE_SIZE,
  });

  const {
    results: rows = [],
    total_count: totalCount = 0,
    total_pages: totalPages = 1,
    per_page: perPage = PAGE_SIZE,
  } = data || {};

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    update({ query: "search", value: e.target.value || null });
  };

  const onPageNumChange = (page: number) => {
    update({ query: "page", value: page });
  };

  const openFormInstance = (row: IncorporationTrackerRow) => {
    if (!row.form_instance_id) {
      return;
    }
    history.push(`/admin/incorporation/tracker/${row.form_instance_id}`);
  };

  const openAssignTemplateModal = (row: IncorporationTrackerRow) => {
    setSelectedRow(row);
    assignModal.open();
  };

  const columnHelper = createColumnHelper<IncorporationTrackerRow>();

  const columns = [
    columnHelper.accessor("company_group_name", {
      header: "Group name",
      size: 25,
      cell: ({ row }) => (
        <IncorporationTrackerNameCell
          groupId={row.original.company_group_id}
          groupName={row.original.company_group_name}
        />
      ),
    }),
    columnHelper.accessor("display_title", {
      header: "Template",
      size: 40,
      cell: ({ row }) => (
        <IncorporationTrackerTemplateCell
          formInstanceId={row.original.form_instance_id}
          displayTitle={row.original.display_title}
          isPublished={row.original.is_published}
          onAssignClick={() => openAssignTemplateModal(row.original)}
          onEditDraftClick={() => openFormInstance(row.original)}
        />
      ),
    }),
    columnHelper.display({
      id: "current_stage",
      header: "Current stage",
      size: 25,
      cell: ({ row }) => (
        <IncorporationTrackerStageCell
          currentStageTitle={row.original.current_stage_title}
          answeredQuestionsCount={row.original.answered_questions_count}
          totalQuestionsCount={row.original.total_questions_count}
          isCompleted={row.original.is_completed}
        />
      ),
    }),
    columnHelper.display({
      id: "notes",
      header: "Notes",
      size: 10,
      cell: ({ row }) => (
        <GroupNote
          groupId={row.original.company_group_id}
          allNotesCount={row.original.notes_count}
        />
      ),
    }),
  ];

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 1 },
  });

  const isEmpty = totalCount === 0;

  return (
    <DashboardLayout
      header={
        <Header
          v2
          title="Tracker"
          bottom={
            <div className="t-w-full t-flex  t-items-center t-justify-between t-pb-4 sm:t-flex-wrap sm:t-justify-end sm:t-gap-3">
              <div className="t-flex t-items-center t-gap-2 sm:t-w-full">
                <Search
                  block
                  onChange={onSearch}
                  value={searchValue || ""}
                  placeholder="Search"
                />
              </div>
              <PaginationHandler
                page_num={pageNumber}
                per_page={perPage}
                total_count={totalCount}
                total_pages={totalPages}
                onPageNumChange={onPageNumChange}
                isLoading={isLoading}
              />
            </div>
          }
        />
      }
      contentProps={{ className: "t-flex-1 t-min-h-0 t-flex t-flex-col" }}
    >
      <div className="t-flex-1 t-min-h-0 t-overflow-auto">
        <Async.Root
          isLoading={isLoading}
          isEmpty={isEmpty}
          isSuccess={isSuccess}
        >
          <Async.Empty>
            <EmptyScreen text="No groups found" />
          </Async.Empty>
          <Async.Success>
            {isMobile ? (
              <div className="t-flex t-flex-col">
                {table.getRowModel().rows.map((row) => (
                  <MobileIncorporationTrackerRow
                    key={row.id}
                    row={row}
                    onClick={openFormInstance}
                  />
                ))}
              </div>
            ) : (
              <div className="t-h-full">
                <TableUI
                  table={table}
                  layout="fixed"
                  sticky
                  height="100%"
                  onRowClick={(row) => openFormInstance(row.original)}
                />
              </div>
            )}
          </Async.Success>
        </Async.Root>
      </div>

      {selectedRow && (
        <AssignTemplateModal
          isOpen={assignModal.isOpen}
          close={assignModal.close}
          groupId={selectedRow.company_group_id}
        />
      )}
    </DashboardLayout>
  );
};
